import { Injectable, inject } from '@angular/core';
import {HttpHeaderService} from './http-header.service';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {APP_CONFIG} from '../config/app-config.token';
import {AppConfig} from '../config/app-config.model';
import {PaymentMomentDetail} from '../model/payment-moment-detail';
import {PaymentsOverview} from '../model/payments-overview';
import {MonthInfo} from '../model/month-info';
import {PaymentMoment} from '../model/payment-moment';
import {UnemploymentEndDate} from '../model/unemployment-end-date';
import {CapacitorHttpService} from './capacitor-http.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private http = inject(HttpClient);
  private capacitorHttp = inject(CapacitorHttpService);
  private httpHeaderService = inject(HttpHeaderService);
  private config = inject<AppConfig>(APP_CONFIG);

  baseurl: string;

  constructor() {
    this.baseurl = this.config.apiGatewayUrl;
  }

  getPaymentsOverviewByMonth(memberId: string, startDateyyyyMM: string, endDateyyyyMM: string): Observable<PaymentsOverview> {
    return this.capacitorHttp.get<PaymentsOverview>(
      this.baseurl + `/members/${memberId}/paymentMonths?startMonth=${startDateyyyyMM}&&endMonth=${endDateyyyyMM}`,
      this.httpHeaderService.getHeaderOptions());
  }

  getDetailByUnemploymentMonth(memberId: string, unemploymentMonth: string): Observable<PaymentMomentDetail[]> {
    return this.capacitorHttp.get<PaymentMomentDetail[]>(
      this.baseurl + `/members/${memberId}/paymentMonthDetail?paymentMonth=${unemploymentMonth}`,
      this.httpHeaderService.getHeaderOptions());
  }

  getDetailByPaymentDate(memberId: string, paymentDate: string): Observable<PaymentMomentDetail[]> {
    return this.capacitorHttp.get<PaymentMomentDetail[]>(this.baseurl + `/members/${memberId}/paymentDateDetail?paymentDate=${paymentDate}`,
      this.httpHeaderService.getHeaderOptions());
  }

  getPaymentsOverviewByDate(memberId: string, startDateyyyyMMdd: string, endDateyyyyMMdd: string): Observable<PaymentsOverview> {
    return this.capacitorHttp.get<PaymentsOverview>(
      this.baseurl + `/members/${memberId}/paymentDates?startDate=${startDateyyyyMMdd}&&endDate=${endDateyyyyMMdd}`,
      this.httpHeaderService.getHeaderOptions());
  }

  getMonthsWithPayments(memberId: string, startMonthyyyyMM: string, endMonthyyyyMM: string): Observable<MonthInfo[]> {
    return this.capacitorHttp.get<MonthInfo[]>(
      this.baseurl + `/members/${memberId}/monthsWithPayments?startMonth=${startMonthyyyyMM}&&endMonth=${endMonthyyyyMM}`,
      this.httpHeaderService.getHeaderOptions());
  }

  getLastPayment(memberId: string): Observable<PaymentMoment> {
    return this.capacitorHttp.get<PaymentMoment>(
      this.baseurl + `/members/${memberId}/payments/last`, this.httpHeaderService.getHeaderOptions());
  }

  getUnemploymentEndDate(memberId: string, region: string): Observable<UnemploymentEndDate[]> {
    return this.capacitorHttp.get<UnemploymentEndDate[]>(
      this.baseurl + `/members/${memberId}/unemployment/allocationRightEndDate?region=${region}`,
      this.httpHeaderService.getHeaderOptions());
  }

  // ========== METHODS FOR EXTERNAL USERS (using /me endpoints) ==========
  // These methods are for external users (members) who can only access their own data.
  // Tyk API Gateway rewrites /members/me to /members/{jwt.preferred_username}
  // Following the pattern from MemberService for consistency.

  /**
   * Get payments overview by month for authenticated external user
   * @param startDateyyyyMM Start month in format YYYY-MM
   * @param endDateyyyyMM End month in format YYYY-MM
   * @returns Observable<PaymentsOverview>
   */
  getMyPaymentsOverviewByMonth(startDateyyyyMM: string, endDateyyyyMM: string): Observable<PaymentsOverview> {
    return this.capacitorHttp.get<PaymentsOverview>(
      this.baseurl + `/members/me/paymentMonths?startMonth=${startDateyyyyMM}&&endMonth=${endDateyyyyMM}`,
      this.httpHeaderService.getHeaderOptions()
    );
  }

  /**
   * Get payment detail by unemployment month for authenticated external user
   * @param unemploymentMonth Unemployment month in format YYYY-MM
   * @returns Observable<PaymentMomentDetail[]>
   */
  getMyDetailByUnemploymentMonth(unemploymentMonth: string): Observable<PaymentMomentDetail[]> {
    return this.capacitorHttp.get<PaymentMomentDetail[]>(
      this.baseurl + `/members/me/paymentMonthDetail?paymentMonth=${unemploymentMonth}`,
      this.httpHeaderService.getHeaderOptions()
    );
  }

  /**
   * Get payment detail by payment date for authenticated external user
   * @param paymentDate Payment date in format YYYY-MM-DD
   * @returns Observable<PaymentMomentDetail[]>
   */
  getMyDetailByPaymentDate(paymentDate: string): Observable<PaymentMomentDetail[]> {
    return this.capacitorHttp.get<PaymentMomentDetail[]>(
      this.baseurl + `/members/me/paymentDateDetail?paymentDate=${paymentDate}`,
      this.httpHeaderService.getHeaderOptions()
    );
  }

  /**
   * Get payments overview by date for authenticated external user
   * @param startDateyyyyMMdd Start date in format YYYY-MM-DD
   * @param endDateyyyyMMdd End date in format YYYY-MM-DD
   * @returns Observable<PaymentsOverview>
   */
  getMyPaymentsOverviewByDate(startDateyyyyMMdd: string, endDateyyyyMMdd: string): Observable<PaymentsOverview> {
    return this.capacitorHttp.get<PaymentsOverview>(
      this.baseurl + `/members/me/paymentDates?startDate=${startDateyyyyMMdd}&&endDate=${endDateyyyyMMdd}`,
      this.httpHeaderService.getHeaderOptions()
    );
  }

  /**
   * Get months with payments for authenticated external user
   * @param startMonthyyyyMM Start month in format YYYY-MM
   * @param endMonthyyyyMM End month in format YYYY-MM
   * @returns Observable<MonthInfo[]>
   */
  getMyMonthsWithPayments(startMonthyyyyMM: string, endMonthyyyyMM: string): Observable<MonthInfo[]> {
    return this.capacitorHttp.get<MonthInfo[]>(
      this.baseurl + `/members/me/monthsWithPayments?startMonth=${startMonthyyyyMM}&&endMonth=${endMonthyyyyMM}`,
      this.httpHeaderService.getHeaderOptions()
    );
  }

  /**
   * Get last payment for authenticated external user
   * @returns Observable<PaymentMoment>
   */
  getMyLastPayment(): Observable<PaymentMoment> {
    return this.capacitorHttp.get<PaymentMoment>(
      this.baseurl + '/members/me/payments/last',
      this.httpHeaderService.getHeaderOptions()
    );
  }

  /**
   * Get unemployment end date for authenticated external user
   * @param region Region code
   * @returns Observable<UnemploymentEndDate[]>
   */
  getMyUnemploymentEndDate(region: string): Observable<UnemploymentEndDate[]> {
    return this.capacitorHttp.get<UnemploymentEndDate[]>(
      this.baseurl + `/members/me/unemployment/allocationRightEndDate?region=${region}`,
      this.httpHeaderService.getHeaderOptions()
    );
  }
}
